import { type ReactNode, useState } from 'react';
import { Trans } from '@lingui/react';
import { AlertDialog } from '../../../../../../components/simple-dialogs/AlertDialog';
import { type ArrayElement } from '@startuptools/common/common';
import {
  GqlDeleteShareTransactionGroupMutation,
  GqlDeleteShareTransactionsMutation,
  type GqlShareTransaction,
} from '../../../../../../graphql/react-operations';
import { type GqlShareTransactionGroupsQuery, ShareTransactionGroupsDocument } from '../../../Transactions.graphql';
import {
  useDeleteShareTransactionGroupMutation,
  useDeleteShareTransactionsMutation,
} from '../../../../../../graphql/react-operations';
import { AlertConfirmDialog } from '../../../../../../components/simple-dialogs/AlertConfirmDialog';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Alert } from '@startuptools/ui/Alert';
import { TransactionsViewDocument } from '../../../transactions-view.graphql';
import { ShareTransactionGroupViewDocument } from '../../../TransactionGroup/transaction-group.graphql';
import { SideNavDocument } from '../../../../../../components/side-nav/side-nav.graphql';
import React from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from '../../../../../../components/Navigate';

export const DeleteTransactions = ({
  transactionGroup,
  shareTransactions,
  forbiddenToDeleteStg,
  selectedTransactions,
  setSelectedTransactions,
  onClose,
  open,
}: {
  transactionGroup: ArrayElement<GqlShareTransactionGroupsQuery['shareTransactionGroups']>;
  shareTransactions: Pick<GqlShareTransaction, 'id'>[];
  forbiddenToDeleteStg: boolean;
  selectedTransactions: string[];
  setSelectedTransactions: React.Dispatch<React.SetStateAction<string[]>>;
  onClose: () => void;
  open: false | 'group' | 'transactions';
}) => {
  const { navigate } = useNavigate();

  const companyId = useCompanyId();
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [autUpdateTransactionCount, setAutUpdateTransactionCount] = useState<number | null>(null);

  // eslint-disable-next-line sut/loading-mutation
  const [deleteShareTransactionGroup] = useDeleteShareTransactionGroupMutation({
    refetchQueries: [
      ShareTransactionGroupsDocument,
      TransactionsViewDocument,
      ShareTransactionGroupViewDocument,
      SideNavDocument,
    ],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteShareTransactions] = useDeleteShareTransactionsMutation({
    refetchQueries: [
      ShareTransactionGroupsDocument,
      TransactionsViewDocument,
      ShareTransactionGroupViewDocument,
      SideNavDocument,
    ],
  });

  const handleDeleteSelected = async () => {
    const isDeletingGroup =
      open === 'group' || (selectedTransactions.length === shareTransactions.length && !forbiddenToDeleteStg);
    try {
      let res:
        | GqlDeleteShareTransactionGroupMutation['deleteShareTransactionGroup']
        | GqlDeleteShareTransactionsMutation['deleteShareTransactions']
        | undefined;
      if (isDeletingGroup) {
        const response = await deleteShareTransactionGroup({
          variables: {
            companyId,
            input: {
              id: transactionGroup.id,
              confirm,
            },
          },
        });
        res = response.data?.deleteShareTransactionGroup;
      } else {
        const response = await deleteShareTransactions({
          variables: {
            companyId,
            input: {
              ids: selectedTransactions,
              confirm,
            },
          },
        });
        res = response.data?.deleteShareTransactions;
      }

      if (!confirm && res && res.awaitingConfirmation) {
        const { deletedIds, updatedSts } = res;

        const toBeDeleted = deletedIds.length + updatedSts.length - selectedTransactions.length;

        if (toBeDeleted < 0) {
          const error = new Error('Negative toBeDeleted');
          Sentry.captureException(error, {
            data: {
              selectedTransactions,
            },
          });
          throw error;
        } else {
          setConfirm(true);
          setAutUpdateTransactionCount(toBeDeleted);
          return;
        }
      }
      if (isDeletingGroup) {
        void navigate('/share-register/transactions');
      }
      setSelectedTransactions([]);
      onClose();
    } catch {
      onClose();
      setErrorMessage(
        <Trans
          id="An error occurred while deleting the transactions. Try it again and if the error persists, contact our
          customer support."
        />,
      );
    }
  };

  return (
    <>
      {open && (
        <AlertConfirmDialog
          onClose={onClose}
          onConfirm={handleDeleteSelected}
          title={<Trans id="Deleting transactions" />}
          content={
            <>
              {open === 'transactions' ? (
                <Trans id="Are you sure you want to delete the selected transactions?" />
              ) : (
                <Trans id="Are you sure you want to delete these transactions?" />
              )}
              {autUpdateTransactionCount && (
                <Alert color="warning">
                  <Trans
                    id="The transactions you want to delete have {succeeding, plural, one {# transaction} other {# transactions}} linked to it. if you delete them, these will be affected (deleted or changed). Confirm that you want to really continue."
                    values={{
                      succeeding: autUpdateTransactionCount,
                    }}
                  />
                </Alert>
              )}
            </>
          }
          confirmButtonText={<Trans id="Delete" />}
        />
      )}

      {errorMessage && (
        <AlertDialog
          title={<Trans id="Error while Deleting Transactions" />}
          onAccept={() => {
            setErrorMessage(null);
          }}
        >
          {errorMessage}
        </AlertDialog>
      )}
    </>
  );
};
